import cs from 'classnames';
import React, { RefObject } from 'react';
import uuidv4 from 'uuid/v4';
import { injectV2 } from '../../../../injectV2';
import { usePreventFocusOnClick } from '../../../../hooks/usePreventFocusOnClick';
import { getEditorLink, getViewerLink } from '../../../../../utils/templateLinks';
import { TemplateVideo } from '../TemplateVideo/TemplateVideo';
import { TemplateImage } from '../../TemplateImage/TemplateImage';
import { Template as ApiTemplate } from '../../../../../../types/api';
import { Close } from '../../../../../Icons/dist';
import { ConfigStore } from '../../../../stores/ConfigStore';
import { RoutingStore } from '../../../../stores/RoutingStore';
import { BaseURLStore } from '../../../../stores/BaseURLStore';
import { UiLoggerStore } from '../../../../stores/UiLoggerStore';
import { useOnClickOutside } from '../../../../hooks/useOnClickOutside';
import { useOnEscPress } from '../../../../hooks/useOnEscPress';
import { useTimeout } from '../../../../hooks/useTimeout';
import { ExperimentsStore } from '../../../../../stores/ExperimentsStore';
import { WithTranslations, withTranslations } from '../../../../../utils/withTranslations';
import s from './ModalBox.scss';
import { modalBoxDataHooks } from './ModalBox.dataHooks';

const boxAnimationDuration = parseInt(s.boxAnimationDuration, 10);

interface ModalBoxProps extends WithTranslations {
  onClose: () => void;
  dataHook?: string;
  videoRef: RefObject<HTMLVideoElement>;
  template: ApiTemplate;
  isVisible: boolean;
  isShowVideoControls: boolean;
  galleryDocIndex: number;
  configStore: ConfigStore;
  routingStore: RoutingStore;
  baseURLStore: BaseURLStore;
  uiLoggerStore: UiLoggerStore;
  categorySlug: string | 'search' | null;
  subCategorySlug: string | null;
  experimentsStore: ExperimentsStore;
  ariaLabelledBy: string;
}

const ModalBoxCmp: React.FC<ModalBoxProps> = ({
  dataHook,
  template,
  isVisible,
  isShowVideoControls,
  videoRef,
  t,
  onClose,
  galleryDocIndex,
  configStore,
  routingStore,
  experimentsStore,
  baseURLStore,
  uiLoggerStore,
  categorySlug,
  subCategorySlug,
  ariaLabelledBy,
}) => {
  const wrapperRef = React.useRef<HTMLDivElement>();
  const [editorSessionId, setEditorSessionId] = React.useState<string>(uuidv4());
  const generateEditorSessionId = () => setEditorSessionId(uuidv4());
  const viewLinkRef = React.useRef<HTMLAnchorElement>();
  const closeButtonRef = React.useRef<HTMLButtonElement>();

  const { siteId, metaSiteId } = template;
  const originUrl = baseURLStore.buildURL(routingStore.location);
  const isUseMetaSiteCreator = experimentsStore.is('specs.funnel.UseMetaSiteCreator', 'true');
  const isNewIntroEnableAppSuggestions = experimentsStore.is('specs.funnel.NewIntroEnableAppSuggestions', 'true');
  const editorLink = getEditorLink({ template, editorSessionId, isUseMetaSiteCreator, isNewIntroEnableAppSuggestions });
  const viewerLink = getViewerLink({ template, language: configStore.config.currentLanguage, originUrl });

  const closeModalBoxTimeout = useTimeout();
  const focusOnViewLinkTimeout = useTimeout();
  const [isStillVisible, setIsStillVisible] = React.useState(false);

  const closeModalBox = () => {
    if (isVisible) {
      setIsStillVisible(false);
      closeModalBoxTimeout.setTimeout(onClose, boxAnimationDuration);
    }
  };

  React.useEffect(() => {
    if (isVisible) {
      setIsStillVisible(true);
      focusOnViewLinkTimeout.setTimeout(() => viewLinkRef.current.focus(), boxAnimationDuration);
    }
  }, [isVisible]);

  const focusOnViewLinkOnTab = (event: React.KeyboardEvent) => {
    if (!event.nativeEvent.shiftKey && event.nativeEvent.code === 'Tab') {
      event.preventDefault();
      viewLinkRef.current.focus();
    }
  };

  const focusOnCloseButtonOnShiftTab = (event: React.KeyboardEvent) => {
    if (event.nativeEvent.shiftKey && event.nativeEvent.code === 'Tab') {
      event.preventDefault();
      closeButtonRef.current.focus();
    }
  };

  const handleViewLinkClick = () => {
    uiLoggerStore.logViewTemplate({
      siteId,
      galleryDocIndex,
      openingBrowserUrl: viewerLink,
      hasVideo: !!template.videoSrc,
      origin: 'video_preview' as const,
      category: categorySlug,
      subCategory: subCategorySlug,
    });
    closeModalBox();
  };

  const handleEditLinkClick = () => {
    uiLoggerStore.logEditTemplate({
      siteId,
      galleryDocIndex,
      openingBrowserUrl: editorLink,
      metaSiteId,
      editorSessionId,
      hasVideo: !!template.videoSrc,
      origin: 'video_preview' as const,
      category: categorySlug,
      subCategory: subCategorySlug,
    });

    closeModalBox();
    setTimeout(() => generateEditorSessionId());
  };

  useOnClickOutside(wrapperRef, closeModalBox);
  useOnEscPress(closeModalBox);
  const preventFocusOnClick = usePreventFocusOnClick();

  return (
    <div data-hook={dataHook} className={cs(s.background, { [s.visible]: isStillVisible })}>
      <div className={s.box} ref={wrapperRef} role="dialog" aria-labelledby={ariaLabelledBy}>
        <div className={s.content} data-hook={modalBoxDataHooks.content()} aria-hidden="true">
          <TemplateImage
            template={template}
            dataHook={modalBoxDataHooks.image()}
            isLargeImage={true}
            categorySlug={categorySlug}
            subCategorySlug={subCategorySlug}
          />
          {template.videoSrc && (
            <TemplateVideo
              videoRef={videoRef}
              videoSource={template.videoSrc}
              dataHook={modalBoxDataHooks.video()}
              isShowControls={isShowVideoControls}
            />
          )}
        </div>
        <div className={s.footer}>
          <a
            href={viewerLink}
            className={s.view}
            target="_blank"
            onClick={handleViewLinkClick}
            data-hook={modalBoxDataHooks.viewerLink()}
            ref={viewLinkRef}
            onKeyDown={(event) => focusOnCloseButtonOnShiftTab(event)}
          >
            {t('template.modal.viewlink')}
          </a>
          <a
            href={editorLink}
            className={s.edit}
            target="_blank"
            onClick={handleEditLinkClick}
            data-hook={modalBoxDataHooks.editLink()}
          >
            {t('template.edit')}
          </a>
        </div>
        <button
          className={s.close}
          onFocus={preventFocusOnClick}
          onClick={closeModalBox}
          aria-label={t('template.modal.close')}
          onKeyDown={(event) => focusOnViewLinkOnTab(event)}
          ref={closeButtonRef}
        >
          <Close className={s.closeIcon} data-hook={modalBoxDataHooks.closeButton()} />
        </button>
      </div>
    </div>
  );
};

export const ModalBox = withTranslations(
  injectV2('uiLoggerStore', 'configStore', 'routingStore', 'baseURLStore', 'experimentsStore')(ModalBoxCmp),
);
